import { HotelPurchaseModel } from 'api-hooks/order/hotel';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { HotelCheckInStatusEnum } from 'common/constants/enum';
import { generateId } from 'common/utils/string';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  HotelPurchaseAddOnsItemFormType,
  HotelPurchaseFormType,
  HotelPurchaseGuestItemFormType,
  HotelPurchaseHotelItemFormType,
  HotelPurchaseItemFormType,
} from '../components/form-type';

interface Props {
  hotelSale?: HotelSaleModel;
  hotelPurchase?: HotelPurchaseModel;
  defaultValues?: HotelPurchaseFormType;
}

export function addNewAddOnsItem(): HotelPurchaseAddOnsItemFormType {
  return {
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    id: undefined,
    price: undefined,
    priceUnit: undefined,
    unit: undefined,
  } as never as HotelPurchaseAddOnsItemFormType;
}

export function addNewGuestItem(
  isLead = false,
): HotelPurchaseGuestItemFormType {
  return {
    customId: generateId(),
    guestId: '',
    guestName: '',
    id: undefined,
    isLead,
  };
}

export function addNewHotelItem(): HotelPurchaseHotelItemFormType {
  return {
    checkInStatus: HotelCheckInStatusEnum.NotAvailable,
    hotelConfirmationCode: '',
    checkIn: undefined,
    checkOut: undefined,
    customId: generateId(),
    detail: '',
    hotelRoomId: '',
    hotelRoomName: '',
    id: undefined,
    night: undefined,
    pax: undefined,
    price: undefined,
    room: undefined,
  } as never as HotelPurchaseHotelItemFormType;
}

export function addNewHotelSaleItem(): HotelPurchaseItemFormType {
  return {
    bookingCode: '',
    cityTax: '',
    customId: generateId(),
    defaultCheckInOut: {
      in: '',
      out: '',
    },
    hotelAddress: '',
    hotelEmail: '',
    hotelId: '',
    hotelName: '',
    hotelPhone: '',
    hotelPurchaseAddOns: [],
    hotelPurchaseGuests: [addNewGuestItem(true)],
    hotelPurchaseHotels: [addNewHotelItem()],
    id: undefined,
    isCityTax: false,
    isByPax: false,
    remark: '',
  } as never as HotelPurchaseItemFormType;
}

export function tourListToHotelPurchase(
  tourList: TourModel,
): HotelPurchaseFormType {
  const transactionAt = new Date();
  const defaultValues = {
    orderNumber: '',
    saleTotal: 0,
    agentId: '',
    agentStatus: '',
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    code: '',
    commission: 0,
    description: '',
    discount: 0,
    invoiceId: '',
    invoiceRefId: '',
    needApproval: false,
    otherCost: 0,
    paymentChannelId: '',
    refId: undefined,
    parentRefId: '',
    tempTransactionAt: transactionAt,
    transactionAt,
    tourId: tourList.id,
    tourRefId: tourList.refId,
    type: 'tour',
    hotelSale: undefined,
    hotelPurchase: undefined,
    total: 0,
    hotelPurchases: [addNewHotelSaleItem()], //activity purchases will be fill with select sales
  };
  return defaultValues;
}

export default function useGetDefaultValues(props: Props) {
  const { hotelPurchase, hotelSale } = props;
  const defaultValues = React.useMemo<HotelPurchaseFormType>(() => {
    const transactionAt = hotelPurchase?.transactionAt || new Date();
    if (props.defaultValues) return props.defaultValues;
    if (hotelSale) {
      return {
        orderNumber: '',
        saleTotal: hotelSale.total,
        agentId: '',
        agentStatus: '',
        remainingTransactionQuota: null,
        isExceedingQuota: false,
        code: hotelSale.code || '',
        commission: 0,
        description: '',
        discount: hotelSale.discount || 0,
        invoiceId: hotelSale.id,
        invoiceRefId: hotelSale.refId,
        needApproval: false,
        otherCost: hotelSale.otherCost || 0,
        paymentChannelId: '',
        parentRefId: '',
        refId: undefined,
        tempTransactionAt: transactionAt,
        transactionAt,
        tourId: undefined,
        tourRefId: undefined,
        type: 'sales',
        hotelSale,
        hotelPurchase,
        total: 0,
        hotelPurchases: [addNewHotelSaleItem()], //activity purchases will be fill with select sales
      };
    }
    const agent = hotelPurchase?.agent;
    const paymentChannel = hotelPurchase?.paymentChannel;
    const sale = hotelPurchase?.sale;
    const tour = hotelPurchase?.tour;

    const hotelPurchases: HotelPurchaseItemFormType[] =
      hotelPurchase?.hotelPurchases?.map((hp) => {
        const hotelPurchaseAddOns: HotelPurchaseAddOnsItemFormType[] =
          hp.hotelPurchaseAddOns.map((hpao) => {
            const guest = hpao.guest;
            return {
              customId: generateId(),
              description: hpao.description || '',
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: hpao.id,
              price: hpao.price,
              priceUnit: hpao.priceUnit || 0,
              unit: hpao.unit || 0,
            };
          });

        const hotelPurchaseGuests: HotelPurchaseGuestItemFormType[] =
          hp.hotelPurchaseGuests.map((hpg) => {
            const guest = hpg.guest;

            return {
              customId: generateId(),
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: hpg.id,
              isLead: hpg.isLead,
            };
          });

        const hotelPurchaseHotels: HotelPurchaseHotelItemFormType[] =
          hp.hotelPurchaseHotels.map((hph) => {
            const hotelRoom = hph.hotelRoom;
            return {
              checkIn: hph.checkIn,
              checkOut: hph.checkOut,
              customId: generateId(),
              detail: hph.detail || '',
              hotelRoomId: hotelRoom.id,
              hotelRoomName: `${hotelRoom.type} (${hotelRoom.size})`,
              id: hph.id,
              night: hph.night,
              pax: hph.pax || 0,
              price: hph.price,
              room: hph.room,
            };
          });

        const hotel = hp.hotel;

        return {
          bookingCode: hp.bookingCode || '',
          customId: generateId(),
          cityTax: hp.cityTax || '',
          hotelAddress: hotel.address || '',
          hotelEmail: hotel.email || '',
          hotelId: hotel.id,
          hotelName: hotel.name,
          hotelPhone: hotel.phone,
          id: hp.id,
          isCityTax: hp.isCityTax || false,
          isByPax: hp.isByPax || false,
          remark: hp.remark || '',
          defaultCheckInOut: {
            in: '',
            out: '',
          },

          hotelPurchaseAddOns,
          hotelPurchaseGuests,
          hotelPurchaseHotels,
        };
      }) ?? [addNewHotelSaleItem()];

    return {
      agentId: agent?.id || '',
      agentStatus: agent?.status || '',
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      paymentChannelId: paymentChannel?.id || '',
      code: hotelPurchase?.code || '',
      orderNumber: hotelPurchase?.number || '',
      commission: hotelPurchase?.agentCommission?.amount || 0,
      description: hotelPurchase?.description || '',
      discount: hotelPurchase?.discount || 0,
      refId: hotelPurchase?.refId,
      parentRefId: hotelPurchase?.parentPurchase?.id,
      invoiceId: sale?.id,
      tourId: tour?.id,
      tourRefId: hotelPurchase?.tour?.refId,
      invoiceRefId: hotelPurchase?.invoiceRefId,
      needApproval: false,
      transactionAt,
      tempTransactionAt: transactionAt,
      otherCost: hotelPurchase?.otherCost || 0,
      total: hotelPurchase?.total || 0,
      saleTotal: sale?.total || 0,
      type: hotelPurchase?.type || 'sales',
      hotelPurchases,

      hotelPurchase,
      hotelSale,
    };
  }, [hotelPurchase, hotelSale, props.defaultValues]);

  return defaultValues;
}
